import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { motion, AnimatePresence } from 'framer-motion';

const ProtectedRoute = ({ children, requiredRole = null, allowedRoles = null }) => {
  const { user, userProfile, loading } = useAuth();

  // Show curtain animation without any text while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 flex flex-col justify-center items-center text-white relative overflow-hidden">
        {/* Animated Curtains - No text, purely visual loading */}
        <AnimatePresence>
          <>
            {/* Left Curtain - Opens to the left */}
            <motion.div
              initial={{ x: 0 }}
              animate={{ x: '-100%' }}
              transition={{ duration: 1.5, ease: 'easeInOut', delay: 0.3 }}
              className="fixed inset-y-0 left-0 w-1/2 bg-gradient-to-r from-purple-900 via-indigo-900 to-purple-800 z-50"
              style={{
                backgroundImage: 'repeating-linear-gradient(90deg, rgba(0,0,0,0.3) 0px, transparent 2px, transparent 4px, rgba(0,0,0,0.3) 6px)',
                boxShadow: '4px 0 20px rgba(0,0,0,0.5)'
              }}
            />
            
            {/* Right Curtain - Opens to the right */}
            <motion.div
              initial={{ x: 0 }}
              animate={{ x: '100%' }}
              transition={{ duration: 1.5, ease: 'easeInOut', delay: 0.3 }}
              className="fixed inset-y-0 right-0 w-1/2 bg-gradient-to-l from-purple-900 via-indigo-900 to-purple-800 z-50"
              style={{
                backgroundImage: 'repeating-linear-gradient(90deg, rgba(0,0,0,0.3) 0px, transparent 2px, transparent 4px, rgba(0,0,0,0.3) 6px)',
                boxShadow: '-4px 0 20px rgba(0,0,0,0.5)'
              }}
            />
          </>
        </AnimatePresence>
        {/* Logo only - no loading text */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="z-10"
        >
          <div className="inline-flex items-center justify-center">
            <img 
              src="/assets/images/rolecall_logo-1756785358409.png" 
              alt="RoleCall Logo" 
              className="h-64 w-auto object-contain"
            />
          </div>
        </motion.div>
      </div>
    );
  }

  // If not authenticated, redirect to login
  if (!user) {
    return <Navigate to="/login-page" replace />;
  }

  // If user is authenticated but profile is still loading
  if (!userProfile && requiredRole) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-blue-900 to-purple-900 flex flex-col justify-center items-center text-white relative overflow-hidden">
        {/* Animated Curtains - No text, purely visual */}
        <AnimatePresence>
          <>
            {/* Left Curtain */}
            <motion.div
              initial={{ x: 0 }}
              animate={{ x: '-100%' }}
              transition={{ duration: 1.5, ease: 'easeInOut', delay: 0.3 }}
              className="fixed inset-y-0 left-0 w-1/2 bg-gradient-to-r from-purple-900 via-indigo-900 to-purple-800 z-50"
              style={{
                backgroundImage: 'repeating-linear-gradient(90deg, rgba(0,0,0,0.3) 0px, transparent 2px, transparent 4px, rgba(0,0,0,0.3) 6px)',
                boxShadow: '4px 0 20px rgba(0,0,0,0.5)'
              }}
            />
            
            {/* Right Curtain */}
            <motion.div
              initial={{ x: 0 }}
              animate={{ x: '100%' }}
              transition={{ duration: 1.5, ease: 'easeInOut', delay: 0.3 }}
              className="fixed inset-y-0 right-0 w-1/2 bg-gradient-to-l from-purple-900 via-indigo-900 to-purple-800 z-50"
              style={{
                backgroundImage: 'repeating-linear-gradient(90deg, rgba(0,0,0,0.3) 0px, transparent 2px, transparent 4px, rgba(0,0,0,0.3) 6px)',
                boxShadow: '-4px 0 20px rgba(0,0,0,0.5)'
              }}
            />
          </>
        </AnimatePresence>
        {/* Logo only - no loading text */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8, delay: 0.5 }}
          className="z-10"
        >
          <div className="inline-flex items-center justify-center">
            <img 
              src="/assets/images/rolecall_logo-1756785358409.png" 
              alt="RoleCall Logo" 
              className="h-64 w-auto object-contain"
            />
          </div>
        </motion.div>
      </div>
    );
  }

  // If role is required, check if user has the required role
  if (requiredRole) {
    const roleHierarchy = {
      user: 1,
      editor: 2,
      superuser: 3
    };

    const userRoleLevel = roleHierarchy?.[userProfile?.role] || 0;
    const requiredRoleLevel = roleHierarchy?.[requiredRole] || 999;

    if (userRoleLevel < requiredRoleLevel) {
      // User doesn't have required role
      // Regular users (role: 'user') should be redirected to student ecosystem
      if (userProfile?.role === 'user') {
        return <Navigate to="/browse-musicals-content" replace />;
      }
      // Other roles redirect to their appropriate dashboard
      return <Navigate to="/dashboard" replace />;
    }
  }

  // If allowedRoles is specified, check if user's role is in the allowed list
  if (allowedRoles && allowedRoles?.length > 0) {
    if (!allowedRoles?.includes(userProfile?.role)) {
      // User's role is not in the allowed list
      if (userProfile?.role === 'user') {
        return <Navigate to="/the-stage-student-dashboard" replace />;
      }
      return <Navigate to="/dashboard" replace />;
    }
  }

  // User is authenticated and has required role (if any)
  return children;
};

export default ProtectedRoute;